import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import LoginModalBtn from "@/components/auth/modal/LoginModalBtn";
import GroupTransition from "@/components/helpers/GroupTransition/GroupTransition";
import IconMagnifyingGlass from "@/components/icons/IconMagnifyingGlass";
import IconX from "@/components/icons/IconX";
import HeaderProfile from "@/components/layout/Header/HeaderProfile/HeaderProfile.tsx";

import { ROUTES } from "@/config/routes.ts";
import { useGetCategories } from "@/hooks/api/content.hooks.ts";
import { useIsKids } from "@/hooks/useIsKids";
import { cn } from "@/utils/cn.ts";

import HeaderDropDown from "./HeaderDropDown";
import HeaderSearchInput from "./HeaderSearchInput";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBtn from "./NotificationBtn";

const HeaderNavigation = ({ isSearchPage }: { isSearchPage: boolean }): JSX.Element => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { data: categories } = useGetCategories();
  const navigate = useNavigate();

  const token = localStorage.getItem("splay:token");

  const isKids = useIsKids();
  const profileId = localStorage.getItem("splay:profileId");

  const openSearch = () => {
    if (isSearchPage) {
      navigate(ROUTES.MAIN.MAIN);
    } else {
      navigate(ROUTES.SEARCH);
    }
  };

  return (
    <>
      <div className="xl:hidden"></div>
      <nav className={"max-xl:hidden"}>
        {!isSearchPage && (
          <GroupTransition transitionKey={"isSearchPage"}>
            <ul className={"flex items-center gap-[1.88rem]"}>
              {token && (
                <li>
                  <Link
                    to={ROUTES.FAVORITES}
                    aria-label={t("tv-programs")}
                    className={cn(
                      "header-link relative py-4 text-sm/4 font-semibold uppercase text-white hover:text-primary",
                      "after:absolute after:bottom-0 after:left-1/2 after:block after:size-[0.3125rem] after:-translate-x-1/2 after:transform after:rounded-full after:bg-orange-1 after:opacity-0 after:transition-opacity after:content-['']",
                      {
                        "text-on-primary after:opacity-100": pathname === ROUTES.FAVORITES,
                      },
                    )}
                  >
                    {t("General.mine")}
                  </Link>
                </li>
              )}

              {!isKids &&
                categories &&
                categories.slice(0, 3).map((category) => {
                  return (
                    <li key={category.id}>
                      <Link
                        to={ROUTES.MAIN.CATALOG.replace(":id", category.id.toString())}
                        aria-label={category.name}
                        className={cn(
                          "header-link relative py-4 text-sm/4 font-semibold uppercase text-white hover:text-primary",
                          "after:absolute after:bottom-0 after:left-1/2 after:block after:size-[0.3125rem] after:-translate-x-1/2 after:transform after:rounded-full after:bg-orange-1 after:opacity-0 after:transition-opacity after:content-['']",
                          {
                            "text-on-primary after:opacity-100":
                              pathname === ROUTES.MAIN.CATALOG.replace(":id", category.id.toString()),
                          },
                        )}
                      >
                        {category.name}
                      </Link>
                    </li>
                  );
                })}

              <li>
                <Link
                  to={ROUTES.CHANNELS.MAIN}
                  aria-label={t("General.tv-channels")}
                  className={cn(
                    "header-link relative py-4 text-sm/4 font-semibold uppercase text-white hover:text-primary",
                    "after:absolute after:bottom-0 after:left-1/2 after:block after:size-[0.3125rem] after:-translate-x-1/2 after:transform after:rounded-full after:bg-orange-1 after:opacity-0 after:transition-opacity after:content-['']",
                    {
                      "text-on-primary after:opacity-100": pathname === ROUTES.CHANNELS.MAIN,
                    },
                  )}
                >
                  {t("General.tv-channels")}
                </Link>
              </li>

              {!isKids && token && (
                <li>
                  <Link
                    to={ROUTES.SUBS}
                    aria-label={t("tv-programs")}
                    className={cn(
                      "header-link relative py-4 text-sm/4 font-semibold uppercase text-white hover:text-primary",
                      "after:absolute after:bottom-0 after:left-1/2 after:block after:size-[0.3125rem] after:-translate-x-1/2 after:transform after:rounded-full after:bg-orange-1 after:opacity-0 after:transition-opacity after:content-['']",
                      {
                        "text-on-primary after:opacity-100": pathname === ROUTES.SUBS,
                      },
                    )}
                  >
                    {t("subscriptions")}
                  </Link>
                </li>
              )}
              {!isKids && <HeaderDropDown categories={categories} />}
            </ul>
          </GroupTransition>
        )}
      </nav>
      <div className={"flex flex-row items-center gap-3"}>
        <nav>
          <ul className={"flex items-center gap-3 max-lg:gap-2"}>
            <div className="hidden sm:block">
              <HeaderSearchInput isSearchPage={isSearchPage} />
            </div>

            <button
              type={"button"}
              title={t("search")}
              onClick={openSearch}
              className={"relative z-[10] rounded-full p-2 sm:hidden"}
            >
              {isSearchPage ? (
                <IconX className="size-6 stroke-on-primary" />
              ) : (
                <IconMagnifyingGlass className={"size-6 stroke-white"} />
              )}
            </button>

            {token && <NotificationBtn />}

            <div className="md:ml-3">
              <LanguageSwitcher />
            </div>
          </ul>
        </nav>
        {token && profileId ? <HeaderProfile /> : <LoginModalBtn />}
      </div>
    </>
  );
};

export default HeaderNavigation;
